import React, { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension, Mark, Node, markPasteRule, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection, NodeSelection } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import suggestion from './mentionSuggestion';
import './RichEditor.css';
import { useAuth } from '../../context/AuthContext';
import hiveService from '../../services/hive';
import {
  htmlToMarkdown,
  isValidVideoUrl,
  isVideoUrlByContent,
  fetchVideoTitleFromUrl,
  isValidUrl,
  isImageUrl,
  isImageUrlByContent,
  buildCanonicalImageShortcode as buildCanonicalImageShortcodeUtil,
  countCharacters,
  parseHighlightShortcodeAttributes,
  uploadImage,
  searchTenorGifs,
  getTenorImageUrl,
  getVideoEmbedUrl,
  markdownToHtml,
  normalizeAlignmentHtml,
} from './richEditorUtils';

const highlightPasteRegex = /\[(?:highlight|hl)([^\]]*)\]([\s\S]+?)\[\/(?:highlight|hl)\]/gi;
const SUPPORTED_SHORTCODE_PASTE_REGEX = /\[(?:\/)?(?:b|i|u|s|h[1-5]|highlight|hl|blockquote|quote|align(?:=(?:left|center|right))?|img|spoiler|video)\b/i;
const MANAGED_MARK_NAMES = new Set(['bold', 'italic', 'underline', 'strike', 'shortappHighlight']);

const escapeShortcodeAttributeValue = (value = '') => String(value || '')
  .replace(/\\/g, '\\\\')
  .replace(/"/g, '\\"')
  .replace(/\]/g, '&#93;');

const getCursorMarks = (state) => state.selection.$cursor?.marks() ?? [];

const areMarkListsEqual = (left, right) => {
  if (left.length !== right.length) return false;
  return left.every((mark, index) => mark.eq(right[index]));
};

const getManagedMarkSet = (state) => {
  const actualMarks = getCursorMarks(state);
  const storedMarks = state.storedMarks ?? [];
  return [...actualMarks, ...storedMarks].filter((mark, index, marks) => (
    MANAGED_MARK_NAMES.has(mark.type.name)
      && marks.findIndex((candidate) => candidate.eq(mark)) === index
  ));
};

const syncStoredMarksWithCursor = (view, { clearManagedMarks = false, resetBlockFormatting = false } = {}) => {
  const { state } = view;
  const cursor = state.selection.$cursor;
  if (!cursor) return;

  const actualMarks = getCursorMarks(state);
  const nextStoredMarks = clearManagedMarks
    ? actualMarks.filter((mark) => !MANAGED_MARK_NAMES.has(mark.type.name))
    : actualMarks;

  let tr = state.tr;
  let changed = false;

  const currentStoredMarks = state.storedMarks ?? actualMarks;
  if (!areMarkListsEqual(currentStoredMarks, nextStoredMarks)) {
    tr = tr.setStoredMarks(nextStoredMarks);
    changed = true;
  }

  if (resetBlockFormatting) {
    const blockPos = cursor.depth > 0 ? cursor.before(cursor.depth) : state.selection.from;
    const blockNode = state.doc.nodeAt(blockPos);

    if (cursor.parent.type.name === 'heading' && state.schema.nodes.paragraph) {
      tr = tr.setBlockType(state.selection.from, state.selection.from, state.schema.nodes.paragraph);
      changed = true;
    } else if (
      cursor.parent.type.name === 'paragraph' &&
      blockNode?.attrs?.textAlign &&
      blockNode.attrs.textAlign !== 'left'
    ) {
      tr = tr.setNodeMarkup(blockPos, null, { ...blockNode.attrs, textAlign: 'left' });
      changed = true;
    }
  }

  if (changed) {
    view.dispatch(tr);
  }
};

const normalizeImageAlignment = (value = 'left') => {
  const normalized = String(value || 'left').toLowerCase();
  if (normalized === 'center' || normalized === 'right') return normalized;
  return 'left';
};

const parseImageAlignmentFromElement = (element) => {
  if (!element) return 'left';

  const explicit = normalizeImageAlignment(
    element.getAttribute('data-shortapp-align')
      || element.getAttribute('data-align')
      || element.getAttribute('align')
      || ''
  );
  if (explicit !== 'left') return explicit;

  const className = String(element.getAttribute('class') || '');
  if (/\btext-right\b/i.test(className)) return 'right';
  if (/\btext-center\b/i.test(className)) return 'center';

  const style = String(element.getAttribute('style') || '').toLowerCase();
  if (/text-align\s*:\s*right/.test(style)) return 'right';
  if (/text-align\s*:\s*center/.test(style)) return 'center';

  // Handle margin shorthand generated by image render style.
  const marginShorthand = style.match(/margin\s*:\s*([^;]+)/i)?.[1] || '';
  if (marginShorthand) {
    const parts = marginShorthand.trim().split(/\s+/).map((part) => part.toLowerCase());
    const rightToken = parts.length === 1
      ? parts[0]
      : parts.length === 2
        ? parts[1]
        : parts.length === 3
          ? parts[1]
          : parts[1];
    const leftToken = parts.length === 1
      ? parts[0]
      : parts.length === 2
        ? parts[1]
        : parts.length === 3
          ? parts[1]
          : parts[3];

    if (leftToken === 'auto' && rightToken === 'auto') return 'center';
    if (leftToken === 'auto' && rightToken !== 'auto') return 'right';
    if (rightToken === 'auto' && leftToken !== 'auto') return 'left';
  }

  const hasMarginLeftAuto = /margin-left\s*:\s*auto/.test(style);
  const hasMarginRightAuto = /margin-right\s*:\s*auto/.test(style);
  const hasMarginRightZero = /margin-right\s*:\s*0(?:px)?/.test(style);

  if (hasMarginLeftAuto && hasMarginRightAuto) return 'center';
  if (hasMarginLeftAuto && hasMarginRightZero) return 'right';

  return 'left';
};

const getImageAlignmentStyle = (alignment = 'left') => {
  const resolved = normalizeImageAlignment(alignment);
  if (resolved === 'center') {
    return 'display: block; margin: 8px auto;';
  }
  if (resolved === 'right') {
    return 'display: block; margin: 8px 0 8px auto;';
  }
  return 'display: block; margin: 8px auto 8px 0;';
};

const parseShortcodeAlignment = (rawAttrs = '') => {
  const match = String(rawAttrs || '').match(/(?:^|\s)align\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s\]]+))/i);
  return normalizeImageAlignment(match?.[1] || match?.[2] || match?.[3] || 'left');
};

const getImageTargetFromSelection = (state) => {
  const selectedNode = state.selection.node;
  if (selectedNode?.type?.name === 'image') {
    return { pos: state.selection.from, node: selectedNode };
  }

  if (!state.selection.empty) return null;

  const { $from } = state.selection;
  const nodeAfter = $from.nodeAfter;
  if (nodeAfter?.type?.name === 'image') {
    return { pos: $from.pos, node: nodeAfter };
  }

  const nodeBefore = $from.nodeBefore;
  if (nodeBefore?.type?.name === 'image') {
    return { pos: $from.pos - nodeBefore.nodeSize, node: nodeBefore };
  }

  // If cursor is at the start/end of a paragraph, also check adjacent sibling blocks.
  if ($from.depth > 0 && $from.parentOffset === 0) {
    const parentStart = $from.before($from.depth);
    const $parentStart = state.doc.resolve(parentStart);
    const prevSibling = $parentStart.nodeBefore;
    if (prevSibling?.type?.name === 'image') {
      return { pos: parentStart - prevSibling.nodeSize, node: prevSibling };
    }
  }

  if ($from.depth > 0 && $from.parentOffset === $from.parent.content.size) {
    const parentEnd = $from.after($from.depth);
    const $parentEnd = state.doc.resolve(parentEnd);
    const nextSibling = $parentEnd.nodeAfter;
    if (nextSibling?.type?.name === 'image') {
      return { pos: parentEnd, node: nextSibling };
    }
  }

  return null;
};

const ShortappImage = Image.extend({
  inline() {
    return false;
  },

  group() {
    return 'block';
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: 'left',
        parseHTML: (element) => parseImageAlignmentFromElement(element),
        renderHTML: (attributes) => ({
          'data-align': normalizeImageAlignment(attributes.align),
          'data-shortapp-align': normalizeImageAlignment(attributes.align),
        }),
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('shortappImageSelection'),
        props: {
          handleClickOn(view, _pos, node, nodePos, event, direct) {
            if (!direct || node.type.name !== 'image') return false;
            event.preventDefault();
            const tr = view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos));
            view.dispatch(tr.scrollIntoView());
            view.focus();
            return true;
          },
        },
      }),
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const resolvedAlign = normalizeImageAlignment(node.attrs?.align || 'left');
    const mergedStyle = getImageAlignmentStyle(resolvedAlign);
    const { 'data-align': _da, 'data-shortapp-align': _dsa, ...rest } = HTMLAttributes;

    return ['img', mergeAttributes(rest, {
      class: `text-${resolvedAlign}`,
      align: resolvedAlign,
      'data-align': resolvedAlign,
      'data-shortapp-align': resolvedAlign,
      style: mergedStyle,
    })];
  },
});

const ShortappVideoEmbed = Node.create({
  name: 'shortappVideoEmbed',

  group: 'block',

  atom: true,

  selectable: true,

  draggable: false,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('shortappVideoCursorGuard'),
        appendTransaction(_transactions, _oldState, newState) {
          const sel = newState.selection;
          if (!(sel instanceof NodeSelection)) return null;
          if (sel.node?.type?.name !== 'shortappVideoEmbed') return null;

          const afterPos = sel.from + sel.node.nodeSize;
          if (afterPos >= newState.doc.content.size) return null;

          const tr = newState.tr;
          tr.setSelection(TextSelection.near(newState.doc.resolve(afterPos), 1));
          return tr;
        },
      }),
    ];
  },

  addAttributes() {
    return {
      url: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-shortapp-video-url') || element.getAttribute('data-video-url') || '',
      },
      title: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-shortapp-video-title') || '',
      },
      align: {
        default: 'left',
        parseHTML: (element) => normalizeImageAlignment(
          element.getAttribute('data-shortapp-align')
            || element.getAttribute('data-align')
            || element.getAttribute('align')
            || 'left'
        ),
      },
      src: {
        default: '',
        parseHTML: (element) => element.getAttribute('src') || '',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'iframe[data-shortapp-video-embed="1"]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const resolvedAlign = normalizeImageAlignment(node.attrs?.align || 'left');
    const url = String(node.attrs?.url || '').trim();
    const title = String(node.attrs?.title || '').trim();
    const src = String(node.attrs?.src || getVideoEmbedUrl(url)).trim();

    if (!src || !url) {
      return ['a', {
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'shortapp-editor-video-link',
      }, title || url];
    }

    return ['iframe', mergeAttributes(HTMLAttributes, {
      src,
      class: `shortapp-editor-video-embed text-${resolvedAlign}`,
      'data-shortapp-video-embed': '1',
      'data-shortapp-video-url': url,
      'data-shortapp-video-title': title,
      'data-shortapp-align': resolvedAlign,
      'data-align': resolvedAlign,
      frameborder: '0',
      allow: 'accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
      allowfullscreen: 'true',
      loading: 'lazy',
      referrerpolicy: 'strict-origin-when-cross-origin',
    })];
  },
});

const ShortappHighlight = Mark.create({
  name: 'shortappHighlight',

  addAttributes() {
    return {
      textColor: {
        default: 'black',
        parseHTML: (element) => element.getAttribute('data-text-color') || element.style.color || 'black',
      },
      backgroundColor: {
        default: 'yellow',
        parseHTML: (element) => element.getAttribute('data-background-color') || element.style.backgroundColor || 'yellow',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-highlight="true"]',
      },
      {
        tag: 'span',
        getAttrs: (element) => {
          const backgroundColor = element.style?.backgroundColor?.trim();
          if (!backgroundColor) return false;

          return {
            textColor: element.style?.color?.trim() || 'black',
            backgroundColor,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const {
      textColor = 'black',
      backgroundColor = 'yellow',
      ...rest
    } = HTMLAttributes;

    return [
      'span',
      mergeAttributes(rest, {
        'data-highlight': 'true',
        'data-text-color': textColor,
        'data-background-color': backgroundColor,
        style: `background-color: ${backgroundColor}; color: ${textColor}; padding-left: 2px; padding-right: 2px;`,
      }),
      0,
    ];
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: highlightPasteRegex,
        type: this.type,
        getAttributes: (match) => parseHighlightShortcodeAttributes(match[1]),
      }),
    ];
  },
});

const ShortappMention = Mention.extend({
  renderHTML({ node, HTMLAttributes }) {
    return [
      'a',
      mergeAttributes({ 
        href: `/user/${node.attrs.id}`, 
        'data-type': 'mention',
        'data-id': node.attrs.id,
        target: '_blank', 
        rel: 'noopener noreferrer' 
      }, this.options.HTMLAttributes, HTMLAttributes),
      `@${node.attrs.id}`,
    ];
  },
  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() || []),
      new Plugin({
        key: new PluginKey('mentionClick'),
        props: {
          handleClick(view, pos, event) {
            if (event.target.closest('a.mention')) {
              window.open(event.target.closest('a.mention').href, '_blank');
              return true;
            }
            return false;
          },
        },
      }),
    ];
  }
});

// Opening shortcode tag: [b], [i], [u], [s], [h1]-[h5], [highlight ...], [hl ...], [blockquote], [quote], [align=...]
// Longer tags must come before short prefixes like `b`, otherwise [blockquote] is misread as [b].
const openShortcodeRegex = /\[(blockquote|highlight|align=(?:left|center|right)|quote|hl|h[1-5]|b|i|u|s)([^\]]*)\]$/i;

const MARK_NAME_MAP = {
  b: 'bold',
  i: 'italic',
  u: 'underline',
  s: 'strike',
  highlight: 'shortappHighlight',
  hl: 'shortappHighlight',
};

const ShortcodeInputExtension = Extension.create({
  name: 'shortcodeInput',

  addOptions() {
    return {
      onUploadImageRef: { current: null },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const { onUploadImageRef } = this.options;
    return [
      new Plugin({
        key: new PluginKey('shortcodeInput'),
        props: {
          handleDrop(view, event) {
            const files = event.dataTransfer?.files;
            if (!files?.length) return false;
            const imageFile = Array.from(files).find(f => f.type.startsWith('image/'));
            const onUploadImage = onUploadImageRef.current;
            if (!imageFile || !onUploadImage) return false;

            event.preventDefault();
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
            onUploadImage(imageFile).then(url => {
              if (!url) return;
              const { state } = view;
              const insertPos = pos?.pos ?? state.selection.from;
              editor.chain().focus().insertContentAt(insertPos, `<img src="${url}" />`).run();
            }).catch(err => {
              console.error('Drop upload failed:', err);
              alert(err.message || 'Failed to upload dropped image');
            });
            return true;
          },
          handlePaste(view, event) {
            const files = event.clipboardData?.files;
            if (files?.length > 0) {
              const imageFile = Array.from(files).find(f => f.type.startsWith('image/'));
              const onUploadImage = onUploadImageRef.current;
              if (imageFile && onUploadImage) {
                event.preventDefault();
                onUploadImage(imageFile).then(url => {
                  if (url) {
                    editor.chain().focus().insertContent(`<img src="${url}" />`).run();
                  }
                }).catch(err => {
                  console.error('Paste upload failed:', err);
                  alert(err.message || 'Failed to upload pasted image');
                });
                return true;
              }
            }

            const pastedText = event.clipboardData?.getData('text/plain') || '';
            if (!pastedText) return false;

            const insertVideoEmbedAtSelection = (selection, sourceUrl, sourceTitle = '', sourceAlign = 'left') => {
              const st = view.state;
              const nodeType = st.schema.nodes.shortappVideoEmbed;
              const paraType = st.schema.nodes.paragraph;
              const embedUrl = getVideoEmbedUrl(sourceUrl);
              if (!nodeType || !paraType || !embedUrl) return false;

              let replaceFrom = selection.from;
              let replaceTo = selection.to;
              const currentSelection = st.selection;
              if (
                currentSelection.empty
                && currentSelection.$from.parent.type.name === 'paragraph'
                && currentSelection.$from.parent.content.size === 0
                && selection.from === currentSelection.from
                && selection.to === currentSelection.to
              ) {
                replaceFrom = currentSelection.$from.before();
                replaceTo = currentSelection.$from.after();
              }

              const resolvedAlign = normalizeImageAlignment(sourceAlign || 'left');
              const videoNode = nodeType.create({
                src: embedUrl,
                url: sourceUrl,
                title: sourceTitle || '',
                align: resolvedAlign,
              });
              const paraNode = paraType.create();

              let tr = st.tr.replaceWith(replaceFrom, replaceTo, [videoNode, paraNode]);
              const cursorPos = Math.min(replaceFrom + videoNode.nodeSize + 1, tr.doc.content.size - 1);
              tr = tr.setSelection(TextSelection.create(tr.doc, cursorPos));
              view.dispatch(tr.scrollIntoView());
              const postPos = Math.min(cursorPos, view.state.doc.content.size - 1);
              view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, postPos)));
              view.focus();
              return true;
            };

            // Intercept bare image URL pastes before Tiptap's autolink turns them
            // into hyperlinks, which would later serialize as [link] and display
            // the image twice on the blockchain.
            const trimmedPaste = pastedText.trim();
            if (!trimmedPaste.includes('\n') && isImageUrl(trimmedPaste)) {
              event.preventDefault();
              const imgShortcode = `${buildCanonicalImageShortcodeUtil(trimmedPaste, 'left')}\n\n`;
              editor.chain().focus().insertContentAt(
                { from: view.state.selection.from, to: view.state.selection.to },
                normalizeAlignmentHtml(markdownToHtml(imgShortcode))
              ).run();
              return true;
            }

            // Known video providers should be converted to [video] directly.
            if (!trimmedPaste.includes('\n') && isValidVideoUrl(trimmedPaste)) {
              event.preventDefault();
              const selection = { from: view.state.selection.from, to: view.state.selection.to };

              fetchVideoTitleFromUrl(trimmedPaste)
                .then((title) => {
                  const inserted = insertVideoEmbedAtSelection(selection, trimmedPaste, title || '', 'left');
                  if (!inserted) {
                    const titleAttribute = title ? ` title="${escapeShortcodeAttributeValue(title)}"` : '';
                    const videoShortcode = `[video url="${escapeShortcodeAttributeValue(trimmedPaste)}"${titleAttribute}]`;
                    editor.chain().focus().insertContentAt(selection, normalizeAlignmentHtml(markdownToHtml(videoShortcode))).run();
                  }
                })
                .catch(() => {
                  const inserted = insertVideoEmbedAtSelection(selection, trimmedPaste, '', 'left');
                  if (!inserted) {
                    editor.chain().focus().insertContentAt(selection, normalizeAlignmentHtml(markdownToHtml(`[video url="${escapeShortcodeAttributeValue(trimmedPaste)}"]`))).run();
                  }
                });
              return true;
            }

            // Fallback for extensionless image URLs: probe response content-type.
            if (!trimmedPaste.includes('\n') && isValidUrl(trimmedPaste)) {
              event.preventDefault();
              const selection = { from: view.state.selection.from, to: view.state.selection.to };

              isImageUrlByContent(trimmedPaste)
                .then(async (isImageContent) => {
                  if (isImageContent) {
                    const content = `${buildCanonicalImageShortcodeUtil(trimmedPaste, 'left')}\n\n`;
                    editor.chain().focus().insertContentAt(
                      selection,
                      normalizeAlignmentHtml(markdownToHtml(content))
                    ).run();
                    return;
                  }

                  const isVideoContent = await isVideoUrlByContent(trimmedPaste);
                  if (isVideoContent) {
                    const title = await fetchVideoTitleFromUrl(trimmedPaste);
                    const inserted = insertVideoEmbedAtSelection(selection, trimmedPaste, title || '', 'left');
                    if (!inserted) {
                      const titleAttribute = title ? ` title="${escapeShortcodeAttributeValue(title)}"` : '';
                      const content = `[video url="${escapeShortcodeAttributeValue(trimmedPaste)}"${titleAttribute}]`;
                      editor.chain().focus().insertContentAt(selection, normalizeAlignmentHtml(markdownToHtml(content))).run();
                    }
                    return;
                  }

                  editor.chain().focus().insertContentAt(
                    selection,
                    normalizeAlignmentHtml(markdownToHtml(`[link url="${escapeShortcodeAttributeValue(trimmedPaste)}"]${trimmedPaste}[/link]`))
                  ).run();
                })
                .catch(() => {
                  editor.chain().focus().insertContentAt(
                    selection,
                    normalizeAlignmentHtml(markdownToHtml(`[link url="${escapeShortcodeAttributeValue(trimmedPaste)}"]${trimmedPaste}[/link]`))
                  ).run();
                });
              return true;
            }

            if (!SUPPORTED_SHORTCODE_PASTE_REGEX.test(pastedText)) return false;

            event.preventDefault();
            editor.chain().focus().insertContentAt(
              { from: view.state.selection.from, to: view.state.selection.to },
              normalizeAlignmentHtml(markdownToHtml(pastedText))
            ).run();
            return true;
          },

          handleKeyDown(view, event) {
            if (event.key === 'Enter' && !event.shiftKey) {
              const { state } = view;
              const cursor = state.selection.$cursor;
              if (!cursor) return false;

              // Let Tiptap's list extension handle Enter inside list items.
              // This enables: Enter on empty item exits the list, Tab indents, etc.
              if (editor.isActive('listItem')) return false;

              if (editor.isActive('blockquote') && state.schema.nodes.paragraph) {
                event.preventDefault();

                let blockquoteDepth = -1;
                for (let depth = cursor.depth; depth > 0; depth -= 1) {
                  if (cursor.node(depth).type.name === 'blockquote') {
                    blockquoteDepth = depth;
                    break;
                  }
                }

                if (blockquoteDepth > 0) {
                  const blockquotePos = cursor.before(blockquoteDepth);
                  const blockquoteNode = cursor.node(blockquoteDepth);
                  const afterBlockquotePos = blockquotePos + blockquoteNode.nodeSize;

                  let tr = state.tr.insert(afterBlockquotePos, state.schema.nodes.paragraph.create());
                  tr = tr.setSelection(TextSelection.create(tr.doc, afterBlockquotePos + 1));
                  tr = tr.setStoredMarks([]);
                  view.dispatch(tr.scrollIntoView());
                  return true;
                }
              }

              event.preventDefault();

              let tr = state.tr;
              const managedMarks = getManagedMarkSet(state);
              const nextChar = state.doc.textBetween(state.selection.from, state.selection.from + 1, '', '');

              // Remove the trailing plain-space placeholder inserted for inline shortcode entry.
              if (managedMarks.length > 0 && nextChar === ' ') {
                tr = tr.delete(state.selection.from, state.selection.from + 1);
              }

              const splitPos = tr.mapping.map(state.selection.from);
              tr = tr.split(splitPos);

              // After split, 2 tokens are inserted at splitPos (close + open).
              // The first content position of the new block is splitPos + 2 in the post-split doc.
              // Do NOT use tr.mapping.map(splitPos + 1) — that maps an intermediate position
              // through the full chain and lands inside the existing next line instead.
              const nextCursorPos = splitPos + 2;
              tr = tr.setSelection(TextSelection.near(tr.doc.resolve(nextCursorPos)));

              const currentStoredMarks = tr.storedMarks ?? getCursorMarks({
                ...state,
                doc: tr.doc,
                selection: tr.selection,
                storedMarks: tr.storedMarks,
              });
              const filteredStoredMarks = currentStoredMarks.filter((mark) => !MANAGED_MARK_NAMES.has(mark.type.name));
              tr = tr.setStoredMarks(filteredStoredMarks);

              const nextCursor = tr.selection.$cursor;
              if (nextCursor?.parent.type.name === 'heading' && tr.doc.type.schema.nodes.paragraph) {
                tr = tr.setBlockType(tr.selection.from, tr.selection.from, tr.doc.type.schema.nodes.paragraph);
              } else if (
                nextCursor?.parent.type.name === 'paragraph'
                && nextCursor.parent.attrs?.textAlign
                && nextCursor.parent.attrs.textAlign !== 'left'
              ) {
                const blockPos = nextCursor.depth > 0 ? nextCursor.before(nextCursor.depth) : tr.selection.from;
                const blockNode = tr.doc.nodeAt(blockPos);
                if (blockNode) {
                  tr = tr.setNodeMarkup(blockPos, null, { ...blockNode.attrs, textAlign: 'left' });
                }
              }

              view.dispatch(tr.scrollIntoView());
              return true;
            }

            if (event.key === 'ArrowRight') {
              const { state } = view;
              const cursor = state.selection.$cursor;
              if (cursor) {
                const managedMarks = getManagedMarkSet(state);
                const nextChar = state.doc.textBetween(state.selection.from, state.selection.from + 1, '', '');

                // Jump across the trailing plain-space placeholder so one ArrowRight exits the inline element.
                if (managedMarks.length > 0 && nextChar === ' ') {
                  event.preventDefault();
                  const tr = state.tr
                    .setSelection(TextSelection.create(state.doc, state.selection.from + 1))
                    .setStoredMarks([]);
                  view.dispatch(tr.scrollIntoView());
                  return true;
                }
              }
            }

            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
              requestAnimationFrame(() => {
                syncStoredMarksWithCursor(view);
              });
            }

            return false;
          },

          handleTextInput(view, from, _to, text) {
            if (text !== ']') return false;

            const { state } = view;
            const $from = state.doc.resolve(from);
            if ($from.parent.type.spec.code) return false;

            const paraText = $from.parent.textBetween(0, $from.parentOffset, null, '\uFFFC');
            const combined = paraText + ']';

            // --- CLOSING TAG ---
            const closeMatch = /\[\/([a-z0-9]+)\]$/i.exec(combined);
            if (closeMatch) {
              const raw = closeMatch[1].toLowerCase();
              const normalizedRaw = raw === 'quote' ? 'blockquote' : raw;
              const tagLen = closeMatch[0].length - 1;
              const tagStart = from - tagLen;
              const { schema } = state;
              const tr = state.tr.delete(tagStart, from);

              const markName = MARK_NAME_MAP[normalizedRaw];
              if (markName && schema.marks[markName]) {
                // Exit the mark: set stored marks without this one
                const existing = state.storedMarks || (state.selection.$cursor?.marks() ?? []);
                tr.setStoredMarks(existing.filter(m => m.type.name !== markName));
                view.dispatch(tr);
                return true;
              }
              if (normalizedRaw === 'blockquote' && schema.nodes.paragraph) {
                tr.setSelection(TextSelection.create(tr.doc, tagStart));
                view.dispatch(tr);
                if (editor.isActive('blockquote')) {
                  editor.chain().focus().toggleBlockquote().run();
                }
                return true;
              }
              if (/^h[1-5]$/.test(normalizedRaw) && schema.nodes.paragraph) {
                tr.setBlockType(tagStart, tagStart, schema.nodes.paragraph);
                view.dispatch(tr);
                return true;
              }
              if (normalizedRaw === 'align' && schema.nodes.paragraph) {
                const $closePos = tr.doc.resolve(tagStart);
                const nodePos = $closePos.before($closePos.depth);
                const node = tr.doc.nodeAt(nodePos);
                if (node) tr.setNodeMarkup(nodePos, null, { ...node.attrs, textAlign: 'left' });
                view.dispatch(tr);
                return true;
              }
              // Unknown close tag — let it through
              return false;
            }

            // --- OPENING TAG ---
            const openMatch = openShortcodeRegex.exec(combined);
            if (!openMatch) return false;

            const rawTag = openMatch[1].toLowerCase();
            const normalizedRawTag = rawTag === 'quote' ? 'blockquote' : rawTag;
            const extraAttrs = openMatch[2] || '';
            const tagLen = openMatch[0].length - 1; // -1: ] not yet in doc
            const tagStart = from - tagLen;
            const { schema } = state;
            const tr = state.tr.delete(tagStart, from);

            // Inline marks
            const markName = MARK_NAME_MAP[normalizedRawTag];
            if (markName && schema.marks[markName]) {
              const markType = schema.marks[markName];
              const attrs = (normalizedRawTag === 'highlight' || normalizedRawTag === 'hl')
                ? parseHighlightShortcodeAttributes(extraAttrs)
                : {};
              const newMark = markType.create(attrs);
              const existing = state.storedMarks || (state.selection.$cursor?.marks() ?? []);
              tr.insertText(' ', tagStart);
              tr.setSelection(TextSelection.create(tr.doc, tagStart));
              tr.setStoredMarks([...existing.filter(m => m.type !== markType), newMark]);
              view.dispatch(tr);
              return true;
            }

            // Blockquote aliases: [blockquote], [quote]
            if (normalizedRawTag === 'blockquote' && schema.nodes.blockquote) {
              const alignment = parseShortcodeAlignment(extraAttrs);
              tr.setSelection(TextSelection.create(tr.doc, tagStart));
              view.dispatch(tr);
              if (!editor.isActive('blockquote')) {
                editor.chain().focus().toggleBlockquote().run();
              }
              if (alignment !== 'left') {
                editor.chain().focus().setTextAlign(alignment).run();
              }
              return true;
            }

            // Headings: [h1]–[h5]
            if (/^h[1-5]$/.test(normalizedRawTag) && schema.nodes.heading) {
              const level = parseInt(normalizedRawTag[1], 10);
              tr.setBlockType(tagStart, tagStart, schema.nodes.heading, { level });
              view.dispatch(tr);
              return true;
            }

            // Alignment: [align=left|center|right]
            if (normalizedRawTag.startsWith('align=') && schema.nodes.paragraph) {
              const alignment = normalizedRawTag.slice(6);
              const $p = tr.doc.resolve(tagStart);
              const nodePos = $p.before($p.depth);
              const node = tr.doc.nodeAt(nodePos);
              if (node) tr.setNodeMarkup(nodePos, null, { ...node.attrs, textAlign: alignment });
              view.dispatch(tr);
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});

/**
 * RichEditor - A comprehensive TipTap-based WYSIWYG editor component
 */
const RichEditor = forwardRef(({
  value = '',
  onChange = () => {},
  onSubmit = null,
  placeholder = 'Write something...',
  maxLength = null,
  toolbar = true,
  onUploadImage = null,
  readOnly = false,
  autoFocus = true,
  defaultMode = 'editor',
  outputFormat = 'markdown',
}, ref) => {
  const { user } = useAuth();

  const handleUploadImageDefault = useCallback(async (file) => {
    if (!user?.username) throw new Error('Login required to upload images');
    return uploadImage(file, user.username, hiveService);
  }, [user]);

  const activeOnUploadImage = onUploadImage || handleUploadImageDefault;
  const onUploadImageRef = useRef(activeOnUploadImage);

  useEffect(() => {
    onUploadImageRef.current = activeOnUploadImage;
  }, [activeOnUploadImage]);

  const normalizeInputContent = useCallback((input) => {
    if (!input) return '';
    const trimmed = String(input).trim();
    const looksLikeHtml = /<(p|h[1-6]|ul|ol|blockquote|table|br\s*\/?)[\s>]/i.test(trimmed) && !/^\s*(?:#|\*|>|-|`)/m.test(trimmed);
    const html = looksLikeHtml ? input : markdownToHtml(input);
    return normalizeAlignmentHtml(html);
  }, []);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTenorSearch, setShowTenorSearch] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showSpoilerModal, setShowSpoilerModal] = useState(false);
  const [showHighlightModal, setShowHighlightModal] = useState(false);
  const [showSyntaxGuide, setShowSyntaxGuide] = useState(false);
  const [editorMode, setEditorMode] = useState(defaultMode);
  const [modalAlignment, setModalAlignment] = useState('left');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [textValue, setTextValue] = useState(() => htmlToMarkdown(normalizeInputContent(value), outputFormat));
  const [emojiSearch, setEmojiSearch] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [spoilerTitle, setSpoilerTitle] = useState('');
  const [spoilerBody, setSpoilerBody] = useState('');
  const [highlightText, setHighlightText] = useState('');
  const [highlightTextColor, setHighlightTextColor] = useState('');
  const [highlightBgColor, setHighlightBgColor] = useState('');
  const [tenorSearch, setTenorSearch] = useState('');
  const [tenorResults, setTenorResults] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [gifError, setGifError] = useState('');
  const [textMentionItems, setTextMentionItems] = useState([]);
  const [textMentionSelectedIndex, setTextMentionSelectedIndex] = useState(0);
  const [textCursorPos, setTextCursorPos] = useState(0);
  
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const gifSearchRef = useRef(null);
  const editorContainerRef = useRef(null);
  const highlightTextInputRef = useRef(null);
  const modalAlignmentRef = useRef('left');
  const [syntaxModalTop, setSyntaxModalTop] = useState(16);

  const updateModalAlignment = useCallback((nextAlignment = 'left') => {
    const resolvedAlignment = normalizeImageAlignment(nextAlignment);
    modalAlignmentRef.current = resolvedAlignment;
    setModalAlignment(resolvedAlignment);
    return resolvedAlignment;
  }, []);

  const syncTextToParent = useCallback((nextText) => {
    onChange(nextText);
  }, [onChange]);

  const confirmAndSubmit = useCallback(() => {
    if (readOnly || typeof onSubmit !== 'function') return false;
    const confirmed = window.confirm('Submit this content?');
    if (!confirmed) return true;
    onSubmit();
    return true;
  }, [onSubmit, readOnly]);

  const escapeShortcodeAttribute = useCallback((value) => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\]/g, '&#93;'), []);

  const getAlignmentShortcodeAttribute = useCallback((alignment = 'left') => (
    ` align="${escapeShortcodeAttribute(normalizeImageAlignment(alignment))}"`
  ), [escapeShortcodeAttribute]);

  const buildAlignedImageShortcode = useCallback((sourceUrl = '', alignment = 'left') => {
    const imageUrl = String(sourceUrl || '').trim();
    if (!imageUrl) return '';

    const canonicalImage = `[img url="${escapeShortcodeAttribute(imageUrl)}"]`;
    const resolvedAlignment = normalizeImageAlignment(alignment || 'left');
    if (resolvedAlignment === 'left') return canonicalImage;
    return `[img url="${escapeShortcodeAttribute(imageUrl)}" align="${resolvedAlignment}"]`;
  }, [escapeShortcodeAttribute]);

  const applyTextSelection = useCallback((prefix = '', suffix = '', placeholderText = '') => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart ?? textValue.length;
    const end = textarea.selectionEnd ?? textValue.length;
    const selected = textValue.slice(start, end);
    const inner = selected || placeholderText;
    const insertion = `${prefix}${inner}${suffix}`;
    const nextText = `${textValue.slice(0, start)}${insertion}${textValue.slice(end)}`;

    setTextValue(nextText);
    syncTextToParent(nextText);

    requestAnimationFrame(() => {
      textarea.focus();
      const cursorStart = start + prefix.length;
      const cursorEnd = cursorStart + inner.length;
      textarea.setSelectionRange(cursorStart, cursorEnd);
    });
  }, [textValue, syncTextToParent]);

  const insertTextAtCursor = useCallback((text) => {
    const textarea = textAreaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart ?? textValue.length;
    const end = textarea.selectionEnd ?? textValue.length;
    const nextText = `${textValue.slice(0, start)}${text}${textValue.slice(end)}`;

    setTextValue(nextText);
    syncTextToParent(nextText);

    requestAnimationFrame(() => {
      textarea.focus();
      // Keep cursor right after the inserted text
      const nextPos = start + text.length;
      textarea.setSelectionRange(nextPos, nextPos);
    });
  }, [textValue, syncTextToParent]);

  const autosizeTextArea = useCallback(() => {
    const textarea = textAreaRef.current;
    if (!textarea || editorMode !== 'text') return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [editorMode]);

  const getTextMentionContext = useCallback((sourceText, cursorPos) => {
    const text = String(sourceText || '');
    const safeCursor = Math.max(0, Math.min(Number(cursorPos) || 0, text.length));
    const beforeCursor = text.slice(0, safeCursor);
    const match = beforeCursor.match(/(^|\s)@([a-z0-9.-]*)$/i);
    if (!match) return null;

    const leading = match[1] || '';
    const query = match[2] || '';
    const replaceStart = safeCursor - match[0].length + leading.length;
    return { query, replaceStart, cursor: safeCursor };
  }, []);

  const clearTextMentions = useCallback(() => {
    setTextMentionItems([]);
    setTextMentionSelectedIndex(0);
  }, []);

  const insertTextMention = useCallback((username) => {
    const textarea = textAreaRef.current;
    if (!textarea || !username) return;

    const cursor = textarea.selectionStart ?? textCursorPos;
    const context = getTextMentionContext(textValue, cursor);
    if (!context) return;

    const mentionText = `@${username} `;
    const nextText = `${textValue.slice(0, context.replaceStart)}${mentionText}${textValue.slice(context.cursor)}`;
    const nextCursor = context.replaceStart + mentionText.length;

    setTextValue(nextText);
    onChange(nextText);
    setTextCursorPos(nextCursor);
    clearTextMentions();

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
    });
  }, [clearTextMentions, getTextMentionContext, onChange, textCursorPos, textValue]);

  const emojiCatalog = [
    { symbol: '😀', name: 'grinning', keywords: 'smile happy face' },
    { symbol: '😁', name: 'beaming', keywords: 'smile happy grin' },
    { symbol: '😂', name: 'tears of joy', keywords: 'laugh lol funny' },
    { symbol: '🤣', name: 'rolling laugh', keywords: 'lol rofl funny' },
    { symbol: '😊', name: 'smiling eyes', keywords: 'blush nice happy' },
    { symbol: '😍', name: 'heart eyes', keywords: 'love crush like' },
    { symbol: '😘', name: 'kiss', keywords: 'love affection' },
    { symbol: '😎', name: 'cool', keywords: 'sunglasses chill' },
    { symbol: '🤔', name: 'thinking', keywords: 'hmm question' },
    { symbol: '😭', name: 'crying', keywords: 'sad tears upset' },
    { symbol: '😡', name: 'angry', keywords: 'mad rage' },
    { symbol: '🔥', name: 'fire', keywords: 'lit hot hype' },
    { symbol: '✨', name: 'sparkles', keywords: 'shine magic' },
    { symbol: '⭐', name: 'star', keywords: 'favorite' },
    { symbol: '🎉', name: 'party popper', keywords: 'celebrate congrats' },
    { symbol: '👏', name: 'clap', keywords: 'applause nice' },
    { symbol: '👍', name: 'thumbs up', keywords: 'yes approve' },
    { symbol: '👎', name: 'thumbs down', keywords: 'no disapprove' },
    { symbol: '🙏', name: 'pray', keywords: 'thanks please' },
    { symbol: '💯', name: 'hundred', keywords: 'perfect keep it real' },
    { symbol: '❤️', name: 'red heart', keywords: 'love heart' },
    { symbol: '💙', name: 'blue heart', keywords: 'heart love' },
    { symbol: '💚', name: 'green heart', keywords: 'heart love' },
    { symbol: '💜', name: 'purple heart', keywords: 'heart love' },
    { symbol: '💔', name: 'broken heart', keywords: 'sad heart' },
    { symbol: '🚀', name: 'rocket', keywords: 'launch ship moon' },
    { symbol: '💡', name: 'light bulb', keywords: 'idea think' },
    { symbol: '📝', name: 'memo', keywords: 'write note' },
    { symbol: '🎬', name: 'clapper board', keywords: 'movie video' },
    { symbol: '📷', name: 'camera', keywords: 'photo picture' },
    { symbol: '🎨', name: 'palette', keywords: 'art design' },
    { symbol: '✅', name: 'check', keywords: 'done ok success' },
    { symbol: '❌', name: 'cross', keywords: 'cancel wrong no' },
    { symbol: '⚠️', name: 'warning', keywords: 'alert caution' },
  ];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: {
          languageClassPrefix: 'language-',
        },
      }),
      Underline,
      ShortappHighlight,
      ShortcodeInputExtension.configure({
        onUploadImageRef,
      }),
      ShortappVideoEmbed,
      ShortappMention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      ShortappImage.configure({
        allowBase64: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: normalizeInputContent(value),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setTextValue(htmlToMarkdown(html));
      onChange(html);
    },
    editable: !readOnly,
    injectCSS: true,
    autofocus: autoFocus ? 'end' : false,
  });

  useEffect(() => {
    if (!editor) return;

    // In text mode, avoid clobbering in-progress user typing with parent echo updates.
    if (editorMode === 'text' && document.activeElement === textAreaRef.current) {
      return;
    }

    // In rich mode, avoid resetting selection/caret while the editor is focused.
    // Parent echo updates can otherwise call setContent and jump the cursor.
    if (editorMode !== 'text' && editor.isFocused) {
      return;
    }

    const normalized = normalizeInputContent(value);
    const current = editor.getHTML();
    if (normalized !== current) {
      editor.commands.setContent(normalized || '', false);
    }
    setTextValue(htmlToMarkdown(normalized));
  }, [editor, value, normalizeInputContent, editorMode]);

  useEffect(() => {
    if (!editor) return;
    if (editorMode === 'text') {
      setTextValue(htmlToMarkdown(editor.getHTML()));
    }
  }, [editorMode, editor]);

  useEffect(() => {
    autosizeTextArea();
  }, [autosizeTextArea, textValue, editorMode]);

  useEffect(() => {
    if (editorMode !== 'text') {
      clearTextMentions();
      return;
    }

    const context = getTextMentionContext(textValue, textCursorPos);
    if (!context || context.query.length < 3) {
      clearTextMentions();
      return;
    }

    let cancelled = false;

    const loadMentions = async () => {
      try {
        const items = await suggestion.items({ query: context.query });
        if (cancelled) return;
        const nextItems = Array.isArray(items) ? items : [];
        setTextMentionItems(nextItems);
        setTextMentionSelectedIndex(0);
      } catch (_err) {
        if (!cancelled) {
          clearTextMentions();
        }
      }
    };

    loadMentions();
    return () => {
      cancelled = true;
    };
  }, [clearTextMentions, editorMode, getTextMentionContext, textCursorPos, textValue]);

  useEffect(() => {
    if (!showEmojiPicker && !showTenorSearch) return undefined;

    const handleOutsideClick = (event) => {
      if (
        showEmojiPicker &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }

      if (
        showTenorSearch &&
        gifSearchRef.current &&
        !gifSearchRef.current.contains(event.target)
      ) {
        setShowTenorSearch(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showEmojiPicker, showTenorSearch]);

  const isAnyModalOpen = showLinkModal || showImageModal || showVideoModal || showSpoilerModal || showHighlightModal || showSyntaxGuide;

  useEffect(() => {
    if (!isAnyModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAnyModalOpen]);

  useEffect(() => {
    if (!showSyntaxGuide) return undefined;

    const updateSyntaxModalTop = () => {
      const container = editorContainerRef.current;
      const rect = container?.getBoundingClientRect?.();
      const nextTop = rect ? Math.max(0, Math.round(rect.top)) : 0;
      setSyntaxModalTop(nextTop);
    };

    updateSyntaxModalTop();
    window.addEventListener('resize', updateSyntaxModalTop);

    return () => {
      window.removeEventListener('resize', updateSyntaxModalTop);
    };
  }, [showSyntaxGuide]);

  useEffect(() => {
    const loadFeatured = async () => {
      if (!showTenorSearch || tenorResults.length > 0) return;
      setLoadingImages(true);
      try {
        const results = await searchTenorGifs('');
        setTenorResults(results);
      } finally {
        setLoadingImages(false);
      }
    };

    loadFeatured();
  }, [showTenorSearch, tenorResults.length]);

  useEffect(() => {
    if (!showHighlightModal) return undefined;

    const timer = window.setTimeout(() => {
      highlightTextInputRef.current?.focus();
      highlightTextInputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [showHighlightModal]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getHTML: () => editor?.getHTML() || '',
    getMarkdown: () => editor?.getText() || '',
    focus: () => editor?.commands.focus(),
    clear: () => editor?.commands.clearContent(),
    setContent: (content) => {
      if (editor) {
        editor.commands.setContent(content);
      }
    },
  }), [editor]);

  const handleEmojiClick = useCallback(
    (emoji) => {
      if (editorMode === 'text') {
        insertTextAtCursor(emoji);
        setShowEmojiPicker(false);
        return;
      }

      if (editor) {
        editor.chain().focus().insertContent(emoji).run();
        setShowEmojiPicker(false);
      }
    },
    [editor, editorMode, insertTextAtCursor]
  );

  const handleHeading = useCallback((level) => {
    if (editorMode === 'text') {
      if (outputFormat === 'shortcode') {
        const prefix = level > 0 ? `[h${level}]` : '';
        const suffix = level > 0 ? `[/h${level}]` : '';
        applyTextSelection(prefix, suffix, 'heading');
      } else {
        const prefix = level > 0 ? `${'#'.repeat(level)} ` : '';
        applyTextSelection(prefix, '', 'heading');
      }
      return;
    }
    if (!editor) return;
    editor.chain().focus().toggleHeading({ level }).run();
  }, [editorMode, outputFormat, applyTextSelection, editor]);

  const getActiveAlignment = useCallback(() => {
    if (!editor) return 'left';

    const imageTarget = getImageTargetFromSelection(editor.state);
    if (imageTarget?.node) {
      return normalizeImageAlignment(imageTarget.node.attrs?.align);
    }

    if (editor.isActive({ textAlign: 'right' })) return 'right';
    if (editor.isActive({ textAlign: 'center' })) return 'center';
    return 'left';
  }, [editor]);

  const isAlignmentActive = useCallback((alignment) => (
    getActiveAlignment() === normalizeImageAlignment(alignment)
  ), [getActiveAlignment]);

  const openLinkModal = useCallback(() => {
    updateModalAlignment(getActiveAlignment());
    setShowLinkModal(true);
  }, [getActiveAlignment, updateModalAlignment]);

  const openImageModal = useCallback(() => {
    updateModalAlignment(getActiveAlignment());
    setImageUrlInput('');
    setShowImageModal(true);
  }, [getActiveAlignment, updateModalAlignment]);

  const closeImageModal = useCallback(() => {
    setShowImageModal(false);
    setImageUrlInput('');
  }, []);

  const openVideoModal = useCallback(() => {
    updateModalAlignment(getActiveAlignment());
    setShowVideoModal(true);
  }, [getActiveAlignment, updateModalAlignment]);

  const openSpoilerModal = useCallback(() => {
    updateModalAlignment(getActiveAlignment());
    setShowSpoilerModal(true);
  }, [getActiveAlignment, updateModalAlignment]);

  const openGifPicker = useCallback(() => {
    updateModalAlignment(getActiveAlignment());
    setShowTenorSearch(true);
  }, [getActiveAlignment, updateModalAlignment]);

  const handleTextAlign = (alignment) => {
    if (editorMode === 'text') {
      const textarea = textAreaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart ?? textValue.length;
      const end = textarea.selectionEnd ?? textValue.length;
      const selected = textValue.slice(start, end);

      // Check if already wrapped with this alignment
      const alignPattern = new RegExp(`^\\[align=${alignment}\\](.*)\\[\\/align\\]$`, 's');
      const trimmedSelected = selected.trim();
      
      if (trimmedSelected && alignPattern.test(trimmedSelected)) {
        // Remove alignment wrapping (toggle off)
        const inner = trimmedSelected.replace(alignPattern, '$1');
        const nextText = `${textValue.slice(0, start)}${inner}${textValue.slice(end)}`;
        setTextValue(nextText);
        syncTextToParent(nextText);

        requestAnimationFrame(() => {
          textarea.focus();
          textarea.setSelectionRange(start, start + inner.length);
        });
      } else if (alignment !== 'left') {
        // Add alignment wrapping (toggle on) - but skip 'left' since it's default
        applyTextSelection(`[align=${alignment}]`, '[/align]', 'text');
      }
      return;
    }

    if (!editor) return;

    const { state, view } = editor;
    const imageTarget = getImageTargetFromSelection(state);

    // Image nodes are not paragraph textblocks, so they need node-level alignment attrs.
    if (imageTarget?.node) {
      const currentAlign = normalizeImageAlignment(imageTarget.node.attrs?.align);
      const nextAlign = currentAlign === alignment ? 'left' : alignment;
      const tr = state.tr.setNodeMarkup(imageTarget.pos, null, {
        ...imageTarget.node.attrs,
        align: nextAlign,
      });
      view.dispatch(tr.scrollIntoView());
      view.focus();
      return;
    }
    
    // Check if current paragraph already has this alignment (toggle behavior)
    if (editor.isActive({ textAlign: alignment })) {
      // Remove alignment by setting to left (default)
      editor.chain().focus().setTextAlign('left').run();
    } else {
      // Set to requested alignment
      editor.chain().focus().setTextAlign(alignment).run();
    }
  };

  const applyBold = useCallback(() => {
    if (editorMode === 'text') {
      const tag = outputFormat === 'shortcode' ? '[b]' : '**';
      const closeTag = outputFormat === 'shortcode' ? '[/b]' : '**';
      applyTextSelection(tag, closeTag, 'bold');
      return;
    }
    if (!editor) return;
    editor.chain().focus().toggleBold().run();
  }, [editorMode, applyTextSelection, editor, outputFormat]);

  const applyItalic = useCallback(() => {
    if (editorMode === 'text') {
      const tag = outputFormat === 'shortcode' ? '[i]' : '*';
      const closeTag = outputFormat === 'shortcode' ? '[/i]' : '*';
      applyTextSelection(tag, closeTag, 'italic');
      return;
    }
    if (!editor) return;
    editor.chain().focus().toggleItalic().run();
  }, [editorMode, applyTextSelection, editor, outputFormat]);

  const applyUnderline = useCallback(() => {
    if (editorMode === 'text') {
      applyTextSelection('<u>', '</u>', 'underline');
      return;
    }
    if (!editor) return;
    editor.chain().focus().toggleUnderline().run();
  }, [editorMode, applyTextSelection, editor]);

  const applyStrike = useCallback(() => {
    if (editorMode === 'text') {
      const tag = outputFormat === 'shortcode' ? '[s]' : '~~';
      const closeTag = outputFormat === 'shortcode' ? '[/s]' : '~~';
      applyTextSelection(tag, closeTag, 'strike');
      return;
    }
    if (!editor) return;
    editor.chain().focus().toggleStrike().run();
  }, [editorMode, applyTextSelection, editor, outputFormat]);

  const closeHighlightModal = useCallback(() => {
    setShowHighlightModal(false);
    setHighlightText('');
    setHighlightTextColor('');
    setHighlightBgColor('');
  }, []);

  const openHighlightModal = useCallback(() => {
    let selectedText = '';

    if (editorMode === 'text') {
      const textarea = textAreaRef.current;
      if (textarea) {
        const start = textarea.selectionStart ?? textValue.length;
        const end = textarea.selectionEnd ?? textValue.length;
        selectedText = textValue.slice(start, end).trim();
      }
    } else if (editor) {
      const { from, to } = editor.state.selection;
      selectedText = editor.state.doc.textBetween(from, to, '').trim();
    }

    setHighlightText(selectedText);
    setHighlightTextColor('');
    setHighlightBgColor('');
    setShowSyntaxGuide(false);
    setShowHighlightModal(true);
  }, [editor, editorMode, textValue]);

  const insertHighlight = useCallback(() => {
    const textColor = highlightTextColor.trim() || 'black';
    const backgroundColor = highlightBgColor.trim() || 'yellow';
    const bodyText = highlightText.trim() || 'highlight';
    const highlightShortcode = `[highlight color="${escapeShortcodeAttribute(textColor)}" bg="${escapeShortcodeAttribute(backgroundColor)}"]${bodyText}[/highlight]`;

    if (editorMode === 'text') {
      insertTextAtCursor(highlightShortcode);
      closeHighlightModal();
      return;
    }

    if (!editor) return;
    const { state, view } = editor;
    const highlightMark = state.schema.marks.shortappHighlight?.create({
      textColor,
      backgroundColor,
    });
    if (!highlightMark) return;

    const highlightNode = state.schema.text(bodyText, [highlightMark]);
    const insertStart = state.selection.from;
    let tr = state.tr.replaceSelectionWith(highlightNode, false);
    // Add a plain trailing space and place caret after it.
    const insertEnd = insertStart + highlightNode.nodeSize;
    tr = tr.setStoredMarks([]);
    tr = tr.insertText(' ', insertEnd);
    tr = tr.setSelection(TextSelection.near(tr.doc.resolve(insertEnd + 1), 1));
    view.dispatch(tr.scrollIntoView());
    view.focus();
    closeHighlightModal();
  }, [closeHighlightModal, editor, editorMode, escapeShortcodeAttribute, highlightBgColor, highlightText, highlightTextColor, insertTextAtCursor]);

  const insertEditorImageWithLinebreak = useCallback((sourceUrl, alignment) => {
    if (!editor) return;
    const imageUrl = String(sourceUrl || '').trim();
    if (!imageUrl) return;

    const selectedAlignment = normalizeImageAlignment(alignment || 'left');
    editor
      .chain()
      .focus()
      .insertContent([
        { type: 'image', attrs: { src: imageUrl, align: selectedAlignment } },
        { type: 'paragraph' },
      ])
      .run();
  }, [editor]);

  const insertEditorVideoWithLinebreak = useCallback((url, title, align) => {
    if (!editor) return;
    const videoUrl = String(url || '').trim();
    if (!videoUrl) return;
    const embedUrl = getVideoEmbedUrl(videoUrl);
    if (!embedUrl) return;

    const resolvedAlignment = normalizeImageAlignment(align || 'left');
    const { state, view } = editor;
    const { from, to, empty, $from } = state.selection;
    let replaceFrom = from;
    let replaceTo = to;

    // If cursor sits in an empty paragraph, replace that whole paragraph so
    // ProseMirror doesn't keep it as a blank line before the block embed.
    if (empty && $from.parent.type.name === 'paragraph' && $from.parent.content.size === 0) {
      replaceFrom = $from.before();
      replaceTo = $from.after();
    }

    const nodeType = state.schema.nodes.shortappVideoEmbed;
    const paraType = state.schema.nodes.paragraph;
    if (!nodeType || !paraType) return;

    const videoNode = nodeType.create({ src: embedUrl, url: videoUrl, title: title || '', align: resolvedAlignment });
    const paraNode = paraType.create();
    let tr = state.tr.replaceWith(replaceFrom, replaceTo, [videoNode, paraNode]);
    const cursorPos = Math.min(replaceFrom + videoNode.nodeSize + 1, tr.doc.content.size - 1);
    tr = tr.setSelection(TextSelection.create(tr.doc, cursorPos));
    view.dispatch(tr.scrollIntoView());
    const postPos = Math.min(cursorPos, view.state.doc.content.size - 1);
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, postPos)));
    view.focus();
  }, [editor]);

  const getCheckedAlignment = useCallback((radioName) => {
    const checked = typeof document !== 'undefined'
      ? document.querySelector(`input[name="${radioName}"]:checked`)
      : null;
    return normalizeImageAlignment(checked?.value || modalAlignmentRef.current || modalAlignment || getActiveAlignment());
  }, [getActiveAlignment, modalAlignment]);

  const insertImage = async (url, alignmentOverride = null) => {
    const imageUrl = String(url || imageUrlInput || '').trim();
    if (!imageUrl) return;

    const selectedAlignment = normalizeImageAlignment(alignmentOverride || getCheckedAlignment('image-align'));
    const imageShortcode = `${buildAlignedImageShortcode(imageUrl, selectedAlignment)}\n\n`;

    if (editorMode === 'text') {
      insertTextAtCursor(imageShortcode);
      closeImageModal();
      return;
    }

    if (editor) {
      insertEditorImageWithLinebreak(imageUrl, selectedAlignment);
      closeImageModal();
    }
  };

  const handleImageUpload = async (e) => {
    const originalFile = e.target.files?.[0];
    if (!originalFile || !editor) return;

    // Mobile WebView Bugfix: Immediately read the file into memory BEFORE any asynchronous
    // `await` calls. If we yield execution to the event loop, Android/iOS WebViews will often 
    // revoke the temporary read permissions for the gallery file, causing 'NotReadableError'.
    const reader = new FileReader();
    const readPromise = new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error(`Failed to cache file: ${reader.error?.message}`));
    });
    reader.readAsArrayBuffer(originalFile);

    try {
      setLoadingImages(true);
      
      const buffer = await readPromise;
      let memoryFile;
      try {
        memoryFile = new File([buffer], originalFile.name, { type: originalFile.type });
      } catch (err) {
        memoryFile = new Blob([buffer], { type: originalFile.type });
        memoryFile.name = originalFile.name;
      }

      const imageUrl = await activeOnUploadImage(memoryFile);
      if (imageUrl) {
        const selectedAlignment = getCheckedAlignment('image-align');
        const imageShortcode = `${buildAlignedImageShortcode(imageUrl, selectedAlignment)}\n\n`;

        if (editorMode === 'text') {
          insertTextAtCursor(imageShortcode);
        } else {
          insertEditorImageWithLinebreak(imageUrl, selectedAlignment);
        }

        closeImageModal();
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert(error.message || 'Failed to upload image');
    } finally {
      setLoadingImages(false);
    }
  };

  const closeVideoModal = useCallback(() => {
    setShowVideoModal(false);
    setVideoTitle('');
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  }, []);

  const resolveVideoTitle = useCallback(async (url = '', selectedTitle = '') => {
    const explicit = videoTitle.trim();
    if (explicit) return explicit;
    if (selectedTitle) return selectedTitle;

    return fetchVideoTitleFromUrl(url);
  }, [videoTitle]);

  const insertVideo = useCallback(async () => {
    const url = videoInputRef.current?.value?.trim();
    let selectedTitle = '';

    if (editorMode === 'text') {
      const textarea = textAreaRef.current;
      if (textarea) {
        const start = textarea.selectionStart ?? textValue.length;
        const end = textarea.selectionEnd ?? textValue.length;
        selectedTitle = textValue.slice(start, end).trim();
      }
    } else if (editor) {
      const { from, to } = editor.state.selection;
      selectedTitle = editor.state.doc.textBetween(from, to, ' ').trim();
    }

    if (!url || !isValidVideoUrl(url)) {
      alert('Please enter a valid video URL.');
      return;
    }

    const effectiveTitle = await resolveVideoTitle(url, selectedTitle);
    const selectedAlignment = getCheckedAlignment('video-align');

    const titleAttribute = effectiveTitle
      ? ` title="${escapeShortcodeAttribute(effectiveTitle)}"`
      : '';
    const alignAttribute = selectedAlignment !== 'left'
      ? getAlignmentShortcodeAttribute(selectedAlignment)
      : '';
    const videoShortcode = `[video url="${escapeShortcodeAttribute(url)}"${titleAttribute}${alignAttribute}]`;

    if (editorMode === 'text') {
      insertTextAtCursor(videoShortcode);
      closeVideoModal();
      return;
    }

    if (!editor) return;
    const embedUrl = getVideoEmbedUrl(url);
    if (embedUrl) {
      insertEditorVideoWithLinebreak(url, effectiveTitle || '', selectedAlignment);
    } else {
      editor.chain().focus().insertContent(normalizeAlignmentHtml(markdownToHtml(videoShortcode))).run();
    }
    closeVideoModal();
  }, [closeVideoModal, editor, editorMode, escapeShortcodeAttribute, getCheckedAlignment, getAlignmentShortcodeAttribute, insertEditorVideoWithLinebreak, insertTextAtCursor, resolveVideoTitle, textValue]);

  const handleTenorSearch = async () => {
    setGifError('');
    try {
      setLoadingImages(true);
      const results = await searchTenorGifs(tenorSearch.trim());
      setTenorResults(results);
      if (!results.length) {
        setGifError('No GIFs found. Add REACT_APP_TENOR_API_KEY for full Tenor results.');
      }
    } catch (error) {
      setGifError('Unable to load GIFs. Add REACT_APP_TENOR_API_KEY and restart dev server.');
    } finally {
      setLoadingImages(false);
    }
  };

  const insertTenorGif = (gif) => {
    const imageUrl = getTenorImageUrl(gif);
    const imageShortcode = `${buildAlignedImageShortcode(imageUrl, 'left')}

`;
    if (editorMode === 'text') {
      insertTextAtCursor(imageShortcode);
    } else if (editor) {
      insertEditorImageWithLinebreak(imageUrl, 'left');
    }
    closeImageModal();
  };

  const closeSpoilerModal = useCallback(() => {
    setShowSpoilerModal(false);
    setSpoilerTitle('');
    setSpoilerBody('');
  }, []);

  const insertLink = async () => {
    const url = linkUrl.trim();
    const text = linkText.trim() || url;

    if (!url || !isValidUrl(url)) {
      const shouldEdit = window.confirm('Invalid URL. Press OK to edit, or Cancel to close.');
      if (!shouldEdit) {
        setShowLinkModal(false);
      }
      return;
    }

    // If the URL points to an image, insert as [img] rather than [link].
    // Using [link] causes the image to appear twice on the blockchain: once as the
    // link body (which Hive clients auto-embed) and once as the resolved href.
    let treatAsImage = isImageUrl(url);
    if (!treatAsImage) {
      treatAsImage = await isImageUrlByContent(url);
    }

    if (treatAsImage) {
      const imageShortcode = `${buildAlignedImageShortcode(url, 'left')}\n\n`;
      if (editorMode === 'text') {
        insertTextAtCursor(imageShortcode);
      } else if (editor) {
        insertEditorImageWithLinebreak(url, 'left');
      }
      setShowLinkModal(false);
      setLinkText('');
      setLinkUrl('');
      return;
    }

    let treatAsVideo = isValidVideoUrl(url);
    if (!treatAsVideo) {
      treatAsVideo = await isVideoUrlByContent(url);
    }

    if (treatAsVideo) {
      const effectiveTitle = await resolveVideoTitle(url, text);
      const titleAttribute = effectiveTitle
        ? ` title="${escapeShortcodeAttribute(effectiveTitle)}"`
        : '';
      const videoShortcode = `[video url="${escapeShortcodeAttribute(url)}"${titleAttribute}]`;

      if (editorMode === 'text') {
        insertTextAtCursor(videoShortcode);
      } else if (editor) {
        const embedUrl = getVideoEmbedUrl(url);
        if (embedUrl) {
          insertEditorVideoWithLinebreak(url, effectiveTitle || '', 'left');
        } else {
          editor.chain().focus().insertContent(normalizeAlignmentHtml(markdownToHtml(videoShortcode))).run();
        }
      }

      setShowLinkModal(false);
      setLinkText('');
      setLinkUrl('');
      return;
    }

    const linkShortcode = `[link url="${escapeShortcodeAttribute(url)}"]${text}[/link]`;

    if (editorMode === 'text') {
      insertTextAtCursor(linkShortcode);
      setShowLinkModal(false);
      setLinkText('');
      setLinkUrl('');
      return;
    }

    if (editor) {
      editor.chain().focus().insertContent({
        type: 'text',
        text,
        marks: [{ type: 'link', attrs: { href: url } }],
      }).run();
      setShowLinkModal(false);
      setLinkText('');
      setLinkUrl('');
    }
  };

  const insertSpoiler = useCallback(() => {
    const title = spoilerTitle.trim();
    const body = spoilerBody.trim();
    const selectedAlignment = normalizeImageAlignment(modalAlignment || getActiveAlignment());
    const spoilerShortcode = `[spoiler title="${escapeShortcodeAttribute(title)}"${getAlignmentShortcodeAttribute(selectedAlignment)}]${body}[/spoiler]`;

    if (editorMode === 'text') {
      insertTextAtCursor(spoilerShortcode);
      closeSpoilerModal();
      return;
    }

    if (!editor) return;
    const { state, view } = editor;
    const insertStart = state.selection.from;
    const tr = state.tr.insertText(spoilerShortcode, insertStart);
    // Cursor right after the spoiler shortcode
    tr.setSelection(TextSelection.create(tr.doc, insertStart + spoilerShortcode.length));
    view.dispatch(tr.scrollIntoView());
    view.focus();
    closeSpoilerModal();
  }, [closeSpoilerModal, editor, editorMode, escapeShortcodeAttribute, getActiveAlignment, getAlignmentShortcodeAttribute, insertTextAtCursor, modalAlignment, spoilerBody, spoilerTitle]);

  const insertSyntaxSnippet = useCallback((snippetOrAction) => {
    if (!snippetOrAction) return;

    if (editorMode === 'text') {
      insertTextAtCursor(snippetOrAction);
      setShowSyntaxGuide(false);
      return;
    }

    if (editor) {
      const { state, view } = editor;
      const insertStart = state.selection.from;
      const contentToInsert = normalizeAlignmentHtml(snippetOrAction);
      const tr = state.tr.insertText(contentToInsert, insertStart);
      // Cursor right after the inserted snippet
      tr.setSelection(TextSelection.create(tr.doc, insertStart + contentToInsert.length));
      view.dispatch(tr.scrollIntoView());
      view.focus();
      setShowSyntaxGuide(false);
    }
  }, [editorMode, insertTextAtCursor, editor]);

  const handleTextModeChange = (event) => {
    const nextText = event.target.value;
    setTextValue(nextText);
    onChange(nextText);
    setTextCursorPos(event.target.selectionStart ?? nextText.length);
    autosizeTextArea();
  };

  const handleTextAreaCursorChange = useCallback((event) => {
    const target = event.target;
    setTextCursorPos(target.selectionStart ?? target.value.length);
  }, []);

  const handleTextAreaKeyDown = useCallback((event) => {
    const hasMentionSuggestions = textMentionItems.length > 0;

    if (hasMentionSuggestions) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setTextMentionSelectedIndex((prev) => (prev + 1) % textMentionItems.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setTextMentionSelectedIndex((prev) => (prev + textMentionItems.length - 1) % textMentionItems.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertTextMention(textMentionItems[textMentionSelectedIndex]);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        clearTextMentions();
        return;
      }
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      confirmAndSubmit();
    }
  }, [clearTextMentions, confirmAndSubmit, insertTextMention, textMentionItems, textMentionSelectedIndex]);

  const handleEditorShortcutKeyDown = useCallback((event) => {
    if (editorMode !== 'editor') return;
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      confirmAndSubmit();
    }
  }, [confirmAndSubmit, editorMode]);

  const handleHeadingChange = (event) => {
    const level = Number(event.target.value);
    handleHeading(level);
  };

  if (!editor) {
    return <div className="rich-editor-loading">Loading editor...</div>;
  }

  const html = editor.getHTML();
  const charCount = countCharacters(html);
  const isAtLimit = maxLength && charCount >= maxLength;

  const filteredEmojis = emojiCatalog.filter((entry) => {
    const q = emojiSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      entry.symbol.includes(q) ||
      entry.name.toLowerCase().includes(q) ||
      entry.keywords.toLowerCase().includes(q)
    );
  });

  const currentHeadingLevel = editor.isActive('heading', { level: 1 })
    ? 1
    : editor.isActive('heading', { level: 2 })
      ? 2
      : editor.isActive('heading', { level: 3 })
        ? 3
        : editor.isActive('heading', { level: 4 })
          ? 4
          : editor.isActive('heading', { level: 5 })
            ? 5
            : 0;

  return (
    <div className="rich-editor-container" ref={editorContainerRef}>
      {toolbar && (
        <div className="rich-editor-toolbar">
          {/* Text Format */}
          <div className="toolbar-group mode-switch-group">
            <button
              type="button"
              className={`toolbar-btn ${editorMode === 'editor' ? 'active' : ''}`}
              onClick={() => setEditorMode('editor')}
              disabled={readOnly}
            >
              Editor
            </button>
            <button
              type="button"
              className={`toolbar-btn ${editorMode === 'text' ? 'active' : ''}`}
              onClick={() => setEditorMode('text')}
              disabled={readOnly}
            >
              Text
            </button>
          </div>

          <div className="toolbar-group mode-switch-group">
            <button
              type="button"
              className={`toolbar-btn ${showSyntaxGuide ? 'active' : ''}`}
              onClick={() => setShowSyntaxGuide((prev) => !prev)}
              disabled={readOnly}
              title="Allowed syntax"
            >
              Syntax
            </button>
          </div>

          {/* Text Format */}
          <div className="toolbar-group">
            <button
              onClick={applyBold}
              className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
              title="Bold (Ctrl+B)"
              disabled={readOnly}
              type="button"
            >
              <strong>B</strong>
            </button>
            <button
              onClick={applyItalic}
              className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
              title="Italic (Ctrl+I)"
              disabled={readOnly}
              type="button"
            >
              <em>I</em>
            </button>
            <button
              onClick={applyUnderline}
              className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
              title="Underline (Ctrl+U)"
              disabled={readOnly}
              type="button"
            >
              <u>U</u>
            </button>
            <button
              onClick={applyStrike}
              className={`toolbar-btn ${editor.isActive('strike') ? 'active' : ''}`}
              title="Strikethrough"
              disabled={readOnly}
              type="button"
            >
              <s>S</s>
            </button>
          </div>

          {/* Headings */}
          <div className="toolbar-group">
            <select
              id="heading-select"
              className="heading-select"
              value={currentHeadingLevel}
              onChange={handleHeadingChange}
              disabled={readOnly}
            >
              <option value={0}>Paragraph</option>
              <option value={1}>H1</option>
              <option value={2}>H2</option>
              <option value={3}>H3</option>
              <option value={4}>H4</option>
              <option value={5}>H5</option>
            </select>
          </div>

          {/* Lists */}
          <div className="toolbar-group">
            <button
              onClick={() => {
                if (editorMode === 'text') {
                  applyTextSelection('- ', '', 'item');
                  return;
                }
                editor.chain().focus().toggleBulletList().run();
              }}
              className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
              title="Bullet list"
              disabled={readOnly}
              type="button"
            >
              •
            </button>
            <button
              onClick={() => {
                if (editorMode === 'text') {
                  applyTextSelection('1. ', '', 'item');
                  return;
                }
                editor.chain().focus().toggleOrderedList().run();
              }}
              className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
              title="Ordered list"
              disabled={readOnly}
              type="button"
            >
              1.
            </button>
          </div>

          {/* Blocks */}
          <div className="toolbar-group">
            <button
              onClick={() => {
                if (editorMode === 'text') {
                  const selectedAlignment = getActiveAlignment();
                  applyTextSelection(`[blockquote${getAlignmentShortcodeAttribute(selectedAlignment)}]`, '[/blockquote]', 'quote');
                  return;
                }
                const selectedAlignment = getActiveAlignment();
                editor.chain().focus().toggleBlockquote().run();
                if (selectedAlignment !== 'left') {
                  editor.chain().focus().setTextAlign(selectedAlignment).run();
                }
              }}
              className={`toolbar-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
              title="Quote"
              disabled={readOnly}
              type="button"
            >
              ❝
            </button>
            <button
              onClick={() => {
                if (editorMode === 'text') {
                  applyTextSelection('```\n', '\n```', 'code');
                  return;
                }
                editor.chain().focus().toggleCodeBlock().run();
              }}
              className={`toolbar-btn ${editor.isActive('codeBlock') ? 'active' : ''}`}
              title="Code block"
              disabled={readOnly}
              type="button"
            >
              &lt;&gt;
            </button>
          </div>

          {/* Alignment */}
          <div className="toolbar-group">
            <button
              onClick={() => handleTextAlign('left')}
              className={`toolbar-btn ${isAlignmentActive('left') ? 'active' : ''}`}
              title="Align left"
              disabled={readOnly}
              type="button"
            >
              ⬅
            </button>
            <button
              onClick={() => handleTextAlign('center')}
              className={`toolbar-btn ${isAlignmentActive('center') ? 'active' : ''}`}
              title="Align center"
              disabled={readOnly}
              type="button"
            >
              ⬌
            </button>
            <button
              onClick={() => handleTextAlign('right')}
              className={`toolbar-btn ${isAlignmentActive('right') ? 'active' : ''}`}
              title="Align right"
              disabled={readOnly}
              type="button"
            >
              ➡
            </button>
          </div>

          {/* Media */}
          <div className="toolbar-group">
            <button
              onClick={openLinkModal}
              className="toolbar-btn"
              title="Insert link"
              disabled={readOnly}
              type="button"
            >
              🔗
            </button>
            <button
              onClick={openImageModal}
              className="toolbar-btn"
              title="Insert image"
              disabled={readOnly}
              type="button"
            >
              🖼
            </button>
            <button
              onClick={openVideoModal}
              className="toolbar-btn"
              title="Insert video"
              disabled={readOnly}
              type="button"
            >
              🎬
            </button>
          </div>

          {/* Special */}
          <div className="toolbar-group">
            <button
              onClick={openHighlightModal}
              className="toolbar-btn toolbar-btn-highlight"
              title="Highlight"
              disabled={readOnly}
              type="button"
            >
              <span className="toolbar-highlight-label">HL</span>
            </button>
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="toolbar-btn"
              title="Emoji"
              disabled={readOnly}
              type="button"
            >
              😊
            </button>
            <button
              onClick={openGifPicker}
              className="toolbar-btn"
              title="GIF"
              disabled={readOnly}
              type="button"
            >
              GIF
            </button>
            <button
              onClick={openSpoilerModal}
              className="toolbar-btn"
              title="Spoiler"
              disabled={readOnly}
              type="button"
            >
              ⚠️
            </button>
          </div>

          {/* Undo/Redo */}
          <div className="toolbar-group">
            <button
              onClick={() => {
                if (editorMode === 'text') {
                  textAreaRef.current?.focus();
                  return;
                }
                editor.chain().focus().undo().run();
              }}
              disabled={!editor.can().undo() || readOnly}
              className="toolbar-btn"
              title="Undo"
              type="button"
            >
              ↶
            </button>
            <button
              onClick={() => {
                if (editorMode === 'text') {
                  textAreaRef.current?.focus();
                  return;
                }
                editor.chain().focus().redo().run();
              }}
              disabled={!editor.can().redo() || readOnly}
              className="toolbar-btn"
              title="Redo"
              type="button"
            >
              ↷
            </button>
          </div>

          {/* Character Count */}
          {maxLength && (
            <div className={`character-counter ${isAtLimit ? 'limit-reached' : ''}`}>
              {charCount} / {maxLength}
            </div>
          )}
        </div>
      )}

      {/* Editor Content */}
      <div className="rich-editor" onClick={(e) => { if (!e.target.closest('.ProseMirror')) editor?.view?.focus(); }} onKeyDownCapture={handleEditorShortcutKeyDown}>
        {editorMode === 'editor' ? (
          <EditorContent editor={editor} />
        ) : (
          <>
            <textarea
              ref={textAreaRef}
              className="rich-editor-textarea"
              value={textValue}
              onChange={handleTextModeChange}
              onClick={handleTextAreaCursorChange}
              onKeyUp={handleTextAreaCursorChange}
              onSelect={handleTextAreaCursorChange}
              onKeyDown={handleTextAreaKeyDown}
              placeholder={placeholder}
              readOnly={readOnly}
            />
            {textMentionItems.length > 0 && (
              <div className="rich-editor-text-mentions mention-list">
                {textMentionItems.map((item, index) => (
                  <button
                    type="button"
                    key={`${item}-${index}`}
                    className={`mention-item ${index === textMentionSelectedIndex ? 'is-selected' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      insertTextMention(item);
                    }}
                  >
                    <img
                      src={`https://images.hive.blog/u/${item}/avatar/small`}
                      alt={item}
                      className="mention-avatar"
                      onError={(e) => { e.target.src = 'https://images.hive.blog/u/hiveio/avatar/small'; }}
                    />
                    <span className="mention-name">{item}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Link Modal */}
      {showSyntaxGuide && (
        <div
          className="syntax-modal-overlay"
          onClick={() => setShowSyntaxGuide(false)}
        >
          <div
            className="syntax-modal-content"
            style={{ maxHeight: `calc(100vh - ${syntaxModalTop}px)` }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Allowed syntax guide"
          >
            <div className="modal-header">
              <h3>Allowed Syntax</h3>
              <button className="modal-close" onClick={() => setShowSyntaxGuide(false)} type="button">✕</button>
            </div>
            <div className="syntax-guide">
              <div className="syntax-guide-hint">Double-click any syntax item below to insert it.</div>
              <div className="syntax-guide-grid">
                <div>
                  <div className="syntax-guide-subtitle">Toolbar Equivalents</div>
                  <code onDoubleClick={applyBold}>Bold</code>
                  <code onDoubleClick={applyItalic}>Italic</code>
                  <code onDoubleClick={applyUnderline}>Underline</code>
                  <code onDoubleClick={applyStrike}>Strike</code>
                  <code onDoubleClick={openHighlightModal}>Highlight</code>
                  <code onDoubleClick={() => handleHeading(1)}>Heading 1</code>
                  <code onDoubleClick={() => handleHeading(2)}>Heading 2</code>
                  <code onDoubleClick={() => handleHeading(3)}>Heading 3</code>
                  <code onDoubleClick={() => handleHeading(4)}>Heading 4</code>
                  <code onDoubleClick={() => handleHeading(5)}>Heading 5</code>
                  <code onDoubleClick={() => {
                    if (editorMode === 'text') {
                      const selectedAlignment = getActiveAlignment();
                      applyTextSelection(`[blockquote${getAlignmentShortcodeAttribute(selectedAlignment)}]`, '[/blockquote]', 'quote');
                    } else {
                      const selectedAlignment = getActiveAlignment();
                      editor.chain().focus().toggleBlockquote().run();
                      if (selectedAlignment !== 'left') {
                        editor.chain().focus().setTextAlign(selectedAlignment).run();
                      }
                    }
                  }}>Quote</code>
                  <code onDoubleClick={() => {
                    if (editorMode === 'text') {
                      applyTextSelection('```\n', '\n```', 'code');
                    } else {
                      editor.chain().focus().toggleCodeBlock().run();
                    }
                  }}>Code block</code>
                  <code onDoubleClick={() => handleTextAlign('left')}>Align left</code>
                  <code onDoubleClick={() => handleTextAlign('center')}>Align center</code>
                  <code onDoubleClick={() => handleTextAlign('right')}>Align right</code>
                  <code onDoubleClick={openLinkModal}>Insert link</code>
                  <code onDoubleClick={openImageModal}>Insert image</code>
                  <code onDoubleClick={openVideoModal}>Insert video</code>
                  <code onDoubleClick={openSpoilerModal}>Insert spoiler</code>
                </div>
                <div>
                  <div className="syntax-guide-subtitle">Stored Shortcodes</div>
                  <code onDoubleClick={() => insertSyntaxSnippet('[h1]Heading[/h1]')}>[h1]...[/h1]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[h2]Heading[/h2]')}>[h2]...[/h2]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[h3]Heading[/h3]')}>[h3]...[/h3]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[h4]Heading[/h4]')}>[h4]...[/h4]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[h5]Heading[/h5]')}>[h5]...[/h5]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[spoiler title="Spoiler"]Hidden content[/spoiler]')}>[spoiler title="Spoiler"]...[/spoiler]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[blockquote]quote text[/blockquote]')}>[blockquote]quote text[/blockquote]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[blockquote]quote text[/blockquote]')}>[quote]...[/quote] (alias of [blockquote])</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[img url="https://example.com/image.jpg"]')}>[img url="https://.../image.jpg"]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[img url="https://example.com/image.jpg" align="right"]')}>[img url="https://.../image.jpg" align="right"]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[video url="https://youtube.com/watch?v=..."]')}>[video url="https://..."]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[video url="https://youtube.com/watch?v=..." align="right"]')}>[video url="https://..." align="right"]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[align=left]text[/align]')}>[align=left]text[/align]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[align=center]text[/align]')}>[align=center]text[/align]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[align=right]text[/align]')}>[align=right]text[/align]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[align=right]Mixed text and [img url="https://example.com/image.jpg"][/align]')}>[align=right]...mixed content...[/align]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[b]bold[/b]')}>[b]bold[/b]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[i]italic[/i]')}>[i]italic[/i]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[u]underline[/u]')}>[u]underline[/u]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[s]strike[/s]')}>[s]strike[/s]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[highlight]text[/highlight]')}>[highlight]text[/highlight]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[highlight color=white bg=#111111]text[/highlight]')}>[highlight color=... bg=...]...[/highlight]</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[hl color=black bg=yellow]text[/hl]')}>[hl color=... bg=...]...[/hl]</code>
                </div>
                <div>
                  <div className="syntax-guide-subtitle">Markdown Input (Accepted)</div>
                  <code onDoubleClick={() => insertSyntaxSnippet('**bold**')}>**bold**</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('*italic*')}>*italic*</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('~~strike~~')}>~~strike~~</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('# Heading 1')}># Heading 1</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('## Heading 2')}>## Heading 2</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('### Heading 3')}>### Heading 3</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('#### Heading 4')}>#### Heading 4</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('##### Heading 5')}>##### Heading 5</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('---')}>---</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('[text](https://example.com)')}>[text](https://example.com)</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('![alt](https://example.com/image.png)')}>![alt](https://image)</code>
                  <code onDoubleClick={() => insertSyntaxSnippet('>! [Hidden title] Hidden content')}>&gt;! [Hidden title] Hidden content</code>
                </div>
              </div>
              <div className="syntax-guide-note">
                Unsupported tags/shortcodes are sanitized or ignored on render.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Insert Link</h3>
              <button className="modal-close" onClick={() => setShowLinkModal(false)} type="button">✕</button>
            </div>
            <input
              type="text"
              placeholder="Link text"
              className="modal-input"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
            />
            <input
              type="url"
              placeholder="https://example.com"
              className="modal-input"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertLink()}
            />
            <div className="modal-footer">
              <button onClick={() => setShowLinkModal(false)} className="btn-secondary" type="button">Cancel</button>
              <button onClick={insertLink} className="btn-primary" type="button">Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="modal-overlay" onClick={closeImageModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Insert Image</h3>
              <button className="modal-close" onClick={closeImageModal} type="button">✕</button>
            </div>
            <input
              ref={imageInputRef}
              type="url"
              placeholder="https://example.com/image.jpg"
              className="modal-input"
              value={imageUrlInput}
              onChange={(e) => setImageUrlInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertImage(imageUrlInput, modalAlignmentRef.current)}
            />
            <div className="modal-align-label">Alignment</div>
            <div className="modal-align-group" role="radiogroup" aria-label="Image alignment">
              <label className="modal-align-option">
                <input type="radio" name="image-align" value="left" checked={modalAlignment === 'left'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Left</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="image-align" value="center" checked={modalAlignment === 'center'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Center</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="image-align" value="right" checked={modalAlignment === 'right'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Right</span>
              </label>
            </div>
            <small style={{ display: 'block', marginTop: '8px', color: 'var(--text-secondary)' }}>
              Stored as <code>[img url="..."]</code> by default, or <code>[img url="..." align="center|right"]</code> when aligned.
            </small>
            <div className="modal-divider">OR</div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={loadingImages}
              className="file-input-hidden"
              id="image-file-input"
            />
            <label htmlFor="image-file-input" className="file-upload-btn" style={{ cursor: loadingImages ? 'not-allowed' : 'pointer' }}>
              {loadingImages ? 'Uploading...' : 'Upload Image'}
            </label>
            <div className="modal-footer">
              <button onClick={closeImageModal} className="btn-secondary" type="button">Cancel</button>
              <button onClick={() => insertImage(imageUrlInput, modalAlignmentRef.current)} disabled={loadingImages} className="btn-primary" type="button">Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="modal-overlay" onClick={closeVideoModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Insert Video</h3>
              <button className="modal-close" onClick={closeVideoModal} type="button">✕</button>
            </div>
            <input
              type="text"
              placeholder="Title (optional)"
              className="modal-input"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
            />
            <input
              ref={videoInputRef}
              type="url"
              placeholder="https://youtube.com/watch?v=..."
              className="modal-input"
              onKeyPress={(e) => e.key === 'Enter' && insertVideo()}
            />
            <div className="modal-align-label">Alignment</div>
            <div className="modal-align-group" role="radiogroup" aria-label="Video alignment">
              <label className="modal-align-option">
                <input type="radio" name="video-align" value="left" checked={modalAlignment === 'left'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Left</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="video-align" value="center" checked={modalAlignment === 'center'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Center</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="video-align" value="right" checked={modalAlignment === 'right'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Right</span>
              </label>
            </div>
            <small style={{ display: 'block', marginTop: '8px', color: 'var(--text-secondary)' }}>
              YouTube, 3speak, Vimeo, Twitch, Dailymotion<br/>
              Stored as <code>[video url="..."]</code> by default, or <code>[video url="..." align="center|right"]</code> when aligned.
            </small>
            <div className="modal-footer">
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.75em', marginRight: 'auto' }}>
                Not guaranteed to work on other frontends.<br/>
                If no title, attempting to fetch from video URL or generate a fallback title.
              </span>
              <button onClick={closeVideoModal} className="btn-secondary" type="button">Cancel</button>
              <button onClick={insertVideo} className="btn-primary" type="button">Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Spoiler Modal */}
      {showSpoilerModal && (
        <div className="modal-overlay" onClick={closeSpoilerModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Insert Spoiler</h3>
              <button className="modal-close" onClick={closeSpoilerModal} type="button">✕</button>
            </div>
            <input
              type="text"
              placeholder="Spoiler title (optional)"
              className="modal-input"
              value={spoilerTitle}
              onChange={(e) => setSpoilerTitle(e.target.value)}
            />
            <textarea
              className="modal-input"
              rows={4}
              placeholder="Hidden content"
              value={spoilerBody}
              onChange={(e) => setSpoilerBody(e.target.value)}
            />
            <div className="modal-align-label">Alignment</div>
            <div className="modal-align-group" role="radiogroup" aria-label="Spoiler alignment">
              <label className="modal-align-option">
                <input type="radio" name="spoiler-align" value="left" checked={modalAlignment === 'left'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Left</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="spoiler-align" value="center" checked={modalAlignment === 'center'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Center</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="spoiler-align" value="right" checked={modalAlignment === 'right'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Right</span>
              </label>
            </div>
            <div className="modal-footer">
              <button onClick={closeSpoilerModal} className="btn-secondary" type="button">Cancel</button>
              <button onClick={insertSpoiler} className="btn-primary" type="button">Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Highlight Modal */}
      {showHighlightModal && (
        <div className="modal-overlay" onClick={closeHighlightModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Insert Highlight</h3>
              <button className="modal-close" onClick={closeHighlightModal} type="button">✕</button>
            </div>
            <input
              ref={highlightTextInputRef}
              type="text"
              placeholder="Highlighted text"
              className="modal-input"
              value={highlightText}
              onChange={(e) => setHighlightText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertHighlight()}
            />
            <span style={{ display: 'block', marginTop: '8px', color: 'var(--text-primary)', fontSize: '0.85em' }}>
              Colors <span style={{ color: 'var(--text-secondary)', fontSize: '0.8em' }}>(#hexcode accepted too)</span>:
            </span>
            <input
              type="text"
              placeholder="Text color (default: black)"
              className="modal-input"
              value={highlightTextColor}
              onChange={(e) => setHighlightTextColor(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertHighlight()}
            />
            <input
              type="text"
              placeholder="Background color (default: yellow)"
              className="modal-input"
              value={highlightBgColor}
              onChange={(e) => setHighlightBgColor(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertHighlight()}
            />
            <div className="modal-footer">
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.75em', marginRight: 'auto' }}>
                Not guaranteed to work on other frontends.
              </span>
              <button onClick={closeHighlightModal} className="btn-secondary" type="button">Cancel</button>
              <button onClick={insertHighlight} className="btn-primary" type="button">Insert</button>
            </div>
          </div>
        </div>
      )}

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="emoji-picker-container" ref={emojiPickerRef}>
          <div className="emoji-picker-header">
            <input
              type="text"
              className="emoji-search-input"
              placeholder="Search emoji..."
              value={emojiSearch}
              onChange={(e) => setEmojiSearch(e.target.value)}
            />
            <button type="button" className="emoji-close-btn" onClick={() => setShowEmojiPicker(false)}>Close</button>
          </div>
          <div className="emoji-grid">
            {filteredEmojis.map((emoji, i) => (
              <button
                key={i}
                type="button"
                className="emoji-btn"
                onClick={() => handleEmojiClick(emoji.symbol)}
              >
                {emoji.symbol}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* GIF Search */}
      {showTenorSearch && (
        <div className="gif-search-container" ref={gifSearchRef}>
          <div className="gif-search-header">
            <input
              type="text"
              placeholder="Search GIFs..."
              value={tenorSearch}
              onChange={(e) => setTenorSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleTenorSearch()}
              className="gif-search-input"
            />
            <div className="modal-align-label">Alignment</div>
            <div className="modal-align-group modal-align-group-compact" role="radiogroup" aria-label="GIF alignment">
              <label className="modal-align-option">
                <input type="radio" name="gif-align" value="left" checked={modalAlignment === 'left'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Left</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="gif-align" value="center" checked={modalAlignment === 'center'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Center</span>
              </label>
              <label className="modal-align-option">
                <input type="radio" name="gif-align" value="right" checked={modalAlignment === 'right'} onChange={(e) => updateModalAlignment(e.target.value)} />
                <span>Right</span>
              </label>
            </div>
            <button onClick={handleTenorSearch} disabled={loadingImages} className="btn-primary" type="button">
              {loadingImages ? 'Searching...' : 'Search'}
            </button>
            <button onClick={() => setShowTenorSearch(false)} className="btn-secondary" type="button">
              Close
            </button>
          </div>
          {!loadingImages && tenorResults.length === 0 && (
            <div className="gif-empty-state">No GIFs found. Try another search term.</div>
          )}
          {gifError && <div className="gif-empty-state">{gifError}</div>}
          <div className="gif-results">
            {tenorResults.map((gif, idx) => (
              <div
                key={idx}
                className="gif-result-item"
                onClick={() => insertTenorGif(gif)}
              >
                <img
                  src={getTenorImageUrl(gif)}
                  alt={`GIF ${idx}`}
                  className="gif-thumbnail"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

RichEditor.displayName = 'RichEditor';

export default RichEditor;
